import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/images/logo-no-background.png';
import { 
  FaBars, 
  FaTimes, 
  FaPhoneAlt, 
  FaWhatsapp,
  FaArrowRight,
  FaCalendarAlt
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Our Systems', path: '/ready-systems' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-[#010536]/95 backdrop-blur-lg shadow-lg shadow-black/20' 
        : 'bg-gradient-to-r from-[#010536] to-[#020a5c]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Bar */}
        <div className="hidden lg:flex items-center justify-between py-2 text-xs text-white/60 border-b border-white/10">
          <div className="flex items-center gap-6">
            <a href="tel:+254762272152" className="flex items-center gap-2 hover:text-[#fdaa01] transition-colors duration-300">
              <FaPhoneAlt className="text-[#fdaa01]" />
              +254 762 272 152
            </a>
            <a href="mailto:shyeecodes@gmail.com" className="flex items-center gap-2 hover:text-[#fdaa01] transition-colors duration-300">
              <span className="text-[#fdaa01]">✉</span>
              shyeecodes@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Available for Projects
            </span>
          </div>
        </div>

        {/* Main Header */}
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img 
                src={logo} 
                alt="Shyee Codes Logo" 
                className="h-10 w-10 lg:h-12 lg:w-12 object-contain group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-[#fdaa01]/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg lg:text-xl font-extrabold text-white leading-tight">
                Shyee <span className="text-[#fdaa01]">Codes</span>
              </span>
              <span className="text-[10px] lg:text-xs text-white/60 tracking-wider uppercase">
                Digital Excellence Agency
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 group ${
                  isActive(item.path)
                    ? 'text-[#fdaa01] bg-[#fdaa01]/10'
                    : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.name}
                <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#fdaa01] transition-all duration-300 ${
                  isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
            
            <div className="ml-4 flex items-center gap-3">
              <a
                href="https://wa.me/254762272152"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-500 hover:bg-green-500 hover:text-white hover:scale-110 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </a>
              
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/20 hover:shadow-xl hover:shadow-[#fdaa01]/30 hover:scale-105 transition-all duration-300"
              >
                Connect With Us
                <FaArrowRight className="text-xs" />
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-300"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#010536]/98 backdrop-blur-lg border-t border-white/10 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                      isActive(item.path)
                        ? 'text-[#fdaa01] bg-[#fdaa01]/10'
                        : 'text-white/80 hover:text-white hover:bg-white/5'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                    <FaArrowRight className="text-xs opacity-0 group-hover:opacity-100" />
                  </Link>
                </motion.div>
              ))}
              
              <div className="pt-4 space-y-3">
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Connect With Us
                  <FaArrowRight className="text-xs" />
                </Link>
                
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href="tel:+254762272152" 
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-colors duration-300"
                  >
                    <FaPhoneAlt className="text-[#fdaa01]" />
                    Call Now
                  </a>
                  <a 
                    href="https://wa.me/+254762272152" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-500 text-sm font-semibold hover:bg-green-500 hover:text-white transition-colors duration-300"
                  >
                    <FaWhatsapp />
                    WhatsApp
                  </a>
                </div>
                
                <a
                  href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 text-sm font-semibold hover:bg-white/10 transition-colors duration-300"
                >
                  <FaCalendarAlt className="text-[#fdaa01]" />
                  Book Consultation
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;